"use client";

import React, { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import styles from "./TireSizeVariantCard.module.scss";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import Text from "@components/atoms/Text";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";

interface TireSizeVariantCardClientProps {
  title: string;
  fuelType: string;
  transmission: string;
  price: string;
  location: string;
  deliveryDate?: string;
  isExpress?: boolean;
  variantSlug?: string;
  isSellable: boolean;
  isDiscontinued: boolean;
  nonSellable: boolean;
  isLoggedIn: boolean;
  makeName?: string;
  modelName?: string;
  pageType?: string;
  cityName?: string;
  carAvailability?: string;
  section?: string;
}

export default function TireSizeVariantCardClient({
  title,
  fuelType,
  transmission,
  price,
  location,
  deliveryDate,
  isExpress,
  variantSlug,
  isSellable,
  isDiscontinued,
  nonSellable,
  isLoggedIn,
  makeName,
  modelName,
  pageType,
  cityName,
  section,
}: TireSizeVariantCardClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();

  // Hide loader when pathname changes or component unmounts
  useEffect(() => {
    return () => {
      hideLoader();
    };
  }, [pathname, hideLoader]);

  const handleVariantClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    // Track variant click event
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.DIMENSIONS_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_variant",
        section: section || "Variant on tyre size",
        car_availability: isSellable ? "Yes" : isDiscontinued ? "Discontinued" : "No",
        vehicle_make: makeName || "",
        vehicle_model: modelName || "",
        city_name: cityName || "",
      },
    });

    showLoader(`Loading ${title}`);

    // Navigate immediately
    router.push(`/cars/${variantSlug}`);

    // Hide loader after a short delay to ensure it shows
    setTimeout(() => {
      hideLoader();
    }, 1000);
  };

  return (
    <div className={styles.card}>
      <div className={styles.headerWrapper}>
        <div className={styles.header}>
          <Text level="h2" className={styles.title}>
            {title}
          </Text>
          <a
            href={`/cars/${variantSlug}`}
            onClick={handleVariantClick}
            style={{ cursor: "pointer" }}
          >
            <Image
              src={`${assetLoader()}/icons/Arrow_right_dark.svg`}
              alt="tire size image"
              width={16}
              height={16}
              loading="lazy"
              style={{ cursor: "pointer" }}
            />
          </a>
        </div>

        <p className={styles.subtitle}>{`${fuelType} • ${transmission}`}</p>
      </div>

      <div className={styles.priceLocationWrapper}>
        <div className={styles.price}>{price}</div>
        <p className={styles.location}>{location}</p>
      </div>

      {!isSellable && (
        <div className={styles.discontinuedBadge}>
          {isDiscontinued && (
            <>
              <Image
                src={`${assetLoader()}/icons/close-dark.svg`}
                alt="Discontinued"
                width={14}
                height={14}
                className={styles.closeIcon}
              />
              <Text className={styles.discontinuedText}>
                Discontinued
              </Text>
            </>
          )}
          {nonSellable && (
            <>
              <Image
                src={`${assetLoader()}/icons/exclamation-dark.svg`}
                alt="Unavailable"
                width={16}
                height={16}
                className={styles.closeIcon}
              />
              <Text className={styles.discontinuedText}>Unavailable on ACKO Drive</Text>
            </>
          )}
        </div>
      )}

      {isSellable &&
        (isExpress ? (
          <Text className={styles.expressDeliveryBadge}>
            <Image
              src={`${assetLoader()}/images/lightning.png`}
              alt="lightning"
              width={16}
              height={16}
              loading="lazy"
              className={styles.lightningIcon}
            />
            {isLoggedIn && deliveryDate
              ? `Express delivery by ${deliveryDate}`
              : "Express delivery available"}
          </Text>
        ) : (
          <Text
            className={styles.standardDeliveryBadge}
          >{`Standard delivery by ${deliveryDate}`}</Text>
        ))}
    </div>
  );
}
